/**
 * Canonical conversion-funnel analytics.
 *
 * One vocabulary for the whole paid journey so the funnel can be measured
 * end-to-end regardless of which surface the visitor started on:
 *
 *   funnel_membership_viewed → funnel_join_clicked → funnel_checkout_opened
 *   → funnel_payment_started → funnel_payment_completed
 *
 * Every event carries:
 *  - a stable `funnel_id` for the visitor (so steps can be stitched together),
 *  - the visitor's acquisition attribution (UTM / referrer / platform),
 *  - `step` + `step_index` so ordering is unambiguous in queries.
 *
 * Page-level and terminal events are de-duplicated, so a re-render, a poll or
 * a browser back-navigation never inflates the numbers.
 */
import { trackOperational } from "@/lib/track";
import { getAttribution } from "@/lib/attribution";

export type FunnelStep =
  // Coarse steps (kept for continuity with existing dashboards)
  | "membership_viewed"
  | "join_clicked"
  | "checkout_opened"
  | "payment_started"
  | "payment_completed"
  // Granular steps — these separate UX friction / trust / value objections
  | "page_view"
  | "checkout_clicked"
  | "email_entered"
  | "payment_method_selected"
  | "redirect_started"
  | "provider_checkout_returned"
  | "provider_checkout_abandoned"
  | "account_created"
  | "signup_started"
  | "checkout_session_created"
  | "checkout_reached"
  | "checkout_failed"
  | "membership_activated";

const STEP_INDEX: Record<FunnelStep, number> = {
  page_view: 1,
  membership_viewed: 1,
  checkout_clicked: 2,
  join_clicked: 2,
  signup_started: 3,
  email_entered: 3,
  payment_method_selected: 4,
  checkout_opened: 5,
  redirect_started: 5,
  payment_started: 5,
  checkout_session_created: 5,
  checkout_failed: 5,
  checkout_reached: 6,
  provider_checkout_returned: 6,
  provider_checkout_abandoned: 6,
  payment_completed: 7,
  account_created: 8,
  membership_activated: 9,
};


const FUNNEL_ID_KEY = "rt_funnel_id";
const SEEN_KEY = "rt_funnel_seen";
const ORIGIN_CTA_KEY = "rt_funnel_origin_cta";

/**
 * The single membership journey. Pricing comes FIRST: every entry point — "Join Recovery Trauma™",
 * "Start Membership", "Become a Member" — goes straight to account creation,
 * exactly like the "Create Account" button:
 *   1 choose membership → 2 create account → 3 complete payment
 *   → 4 access membership.
 * There is no competing direct-pay funnel.
 */
export const MEMBERSHIP_JOURNEY_PATH = "/choose-membership" as const;

/** Where the membership page's "Create account" button goes. */
export const MEMBERSHIP_SIGNUP_PATH = "/signup" as const;



function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage may be unavailable (private mode, blocked cookies) */
  }
}

/** Stable per-visitor id used to stitch funnel steps together. */
export function getFunnelId(): string {
  if (typeof window === "undefined") return "ssr";
  const existing = safeGet(FUNNEL_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  safeSet(FUNNEL_ID_KEY, id);
  return id;
}

function readSeen(): Record<string, number> {
  const raw = safeGet(SEEN_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return {};
  }
}

/** Returns true the first time a de-dupe key is used, false afterwards. */
function claimOnce(key: string): boolean {
  const seen = readSeen();
  if (seen[key]) return false;
  seen[key] = Date.now();
  safeSet(SEEN_KEY, JSON.stringify(seen));
  return true;
}

export type FunnelContext = {
  /** Where the step happened: "home_hero", "membership_page", "explore", ... */
  source: string;
  /** Payment provider once one is chosen. */
  provider?: "stripe" | "paypal" | "apple";
  plan?: string;
  price_usd?: number;
  /** De-dupe key — when set, the event fires at most once per visitor. */
  dedupeKey?: string;
};

/**
 * Track a canonical funnel step. Safe to call from any component; no-ops
 * during SSR and never throws.
 */
export function trackFunnel(
  step: FunnelStep,
  context: FunnelContext,
  properties: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") return;
  const { source, provider, plan, price_usd, dedupeKey } = context;
  if (dedupeKey && !claimOnce(`${step}:${dedupeKey}`)) return;

  const attr = getAttribution();
  trackOperational(
    `funnel_${step}`,
    {
      ...properties,
      step,
      step_index: STEP_INDEX[step],
      funnel_id: getFunnelId(),
      // Which button originally started this membership journey, carried
      // through every later step so entry points can be compared.
      origin_cta: getOriginCta(),
      provider: provider ?? null,
      plan: plan ?? "membership_monthly",
      price_usd: price_usd ?? 14.99,

      utm_source: attr?.utm_source ?? null,
      utm_medium: attr?.utm_medium ?? null,
      utm_campaign: attr?.utm_campaign ?? null,
      utm_content: attr?.utm_content ?? null,
      utm_term: attr?.utm_term ?? null,
      referrer: attr?.referrer ?? null,
      acquisition_platform: attr?.platform ?? null,
    },
    { source },
  );
}

/** The CTA label that started the current membership journey, if any. */
export function getOriginCta(): string | null {
  if (typeof window === "undefined") return null;
  return safeGet(ORIGIN_CTA_KEY);
}

/**
 * Entry point for the single membership journey.
 *
 * Call from every "Create account" / "Join Recovery Trauma™" /
 * "Start Membership" / "Become a Member" button. It records the funnel start
 * (`funnel_join_clicked`) together with the exact CTA the visitor clicked, and
 * remembers that CTA so later steps (verify email → choose provider → pay)
 * can be attributed back to it.
 */
export function startMembershipJourney(cta: string, source: string): void {
  if (typeof window === "undefined") return;
  safeSet(ORIGIN_CTA_KEY, cta);
  trackFunnel("join_clicked", { source }, { cta, journey: "account_first" });
}

/**
 * Record a failed attempt to send a new account into provider checkout.
 *
 * This is the difference between "the person decided not to pay" and "we
 * never got them to the payment page", so it is always recorded with the
 * real reason, the provider, the plan and the fallback we sent them to.
 */
export function trackCheckoutFailed(input: {
  source: string;
  provider: "stripe" | "paypal" | "apple";
  plan: string;
  price_usd?: number;
  price_id?: string | null;
  reason: string;
  stage: "session_create" | "redirect" | "provider";
  fallback: string;
}): void {
  const { source, provider, plan, price_usd, price_id, reason, stage, fallback } = input;
  trackFunnel(
    "checkout_failed",
    { source, provider, plan, price_usd },
    {
      reason: String(reason).slice(0, 300),
      stage,
      fallback,
      price_id: price_id ?? null,
    },
  );
}
